import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor, ArrowLeft, User, Award } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rank: '',
    years_experience: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setFormData({
        name: res.data.name || '',
        rank: res.data.rank || '',
        years_experience: res.data.years_experience || ''
      });
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.rank) updateData.rank = formData.rank;
      if (formData.years_experience) updateData.years_experience = parseInt(formData.years_experience);

      await axios.patch(`${API}/auth/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleRequestVerification = async () => {
    try {
      await axios.post(`${API}/auth/request-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification request submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request verification');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="profile-loading">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 w-full backdrop-blur-lg bg-background/80 border-b border-border/40 h-16 flex items-center px-4 justify-between">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={() => navigate('/')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Anchor className="w-5 h-5 text-primary" />
          <span className="font-heading font-bold" data-testid="header-app-name">MarineKnow</span>
        </div>
      </header>

      <div className="max-w-md mx-auto w-full p-4">
        <div className="bg-card border border-white/5 rounded-xl p-6 mb-6" data-testid="profile-header">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-secondary-foreground" data-testid="profile-icon" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold" data-testid="profile-title">Profile</h1>
                {profile?.is_verified && (
                  <Badge className="mt-1 bg-primary/20 text-primary border-primary/30" data-testid="verified-badge">
                    <Award className="w-3 h-3 mr-1" />
                    Verified Sailor
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground" data-testid="email-label">Email</p>
              <p className="text-sm" data-testid="email-value">{profile?.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground" data-testid="name-label">Name</p>
              <p className="text-sm" data-testid="name-value">{profile?.name || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground" data-testid="rank-label">Rank</p>
              <p className="text-sm" data-testid="rank-value">{profile?.rank || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground" data-testid="experience-label">Years of Experience</p>
              <p className="text-sm" data-testid="experience-value">{profile?.years_experience || 'Not set'}</p>
            </div>
          </div>

          <Button
            variant="secondary"
            className="w-full mt-4 rounded-full"
            onClick={() => setEditing(true)}
            data-testid="edit-profile-button"
          >
            Edit Profile
          </Button>
        </div>

        {!profile?.is_verified && (
          <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="verification-section">
            <h2 className="text-lg font-heading font-semibold mb-2" data-testid="verification-title">
              Become a Verified Sailor
            </h2>
            <p className="text-sm text-muted-foreground mb-4" data-testid="verification-description">
              Complete your profile (name and rank) to request verification. Verified sailors' reports carry more weight in community data.
            </p>
            <Button
              className="w-full rounded-full"
              onClick={handleRequestVerification}
              disabled={!profile?.name || !profile?.rank}
              data-testid="request-verification-button"
            >
              <Award className="w-4 h-4 mr-2" />
              Request Verification
            </Button>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="edit-modal">
          <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-heading font-bold mb-4" data-testid="edit-modal-title">Edit Profile</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" data-testid="name-input-label">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="mt-1"
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label data-testid="rank-select-label">Rank</Label>
                <Select value={formData.rank} onValueChange={(v) => setFormData({ ...formData, rank: v })}>
                  <SelectTrigger className="mt-1" data-testid="rank-select">
                    <SelectValue placeholder="Select rank" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="OS" data-testid="rank-os">OS (Ordinary Seaman)</SelectItem>
                    <SelectItem value="AB" data-testid="rank-ab">AB (Able Seaman)</SelectItem>
                    <SelectItem value="Cadet" data-testid="rank-cadet">Cadet</SelectItem>
                    <SelectItem value="3O" data-testid="rank-3o">3O (Third Officer)</SelectItem>
                    <SelectItem value="2O" data-testid="rank-2o">2O (Second Officer)</SelectItem>
                    <SelectItem value="C/O" data-testid="rank-co">C/O (Chief Officer)</SelectItem>
                    <SelectItem value="Master" data-testid="rank-master">Master</SelectItem>
                    <SelectItem value="4E" data-testid="rank-4e">4E (Fourth Engineer)</SelectItem>
                    <SelectItem value="3E" data-testid="rank-3e">3E (Third Engineer)</SelectItem>
                    <SelectItem value="2E" data-testid="rank-2e">2E (Second Engineer)</SelectItem>
                    <SelectItem value="C/E" data-testid="rank-ce">C/E (Chief Engineer)</SelectItem>
                    <SelectItem value="ETO" data-testid="rank-eto">ETO (Electro-Technical Officer)</SelectItem>
                    <SelectItem value="Bosun" data-testid="rank-bosun">Bosun</SelectItem>
                    <SelectItem value="Pumpman" data-testid="rank-pumpman">Pumpman</SelectItem>
                    <SelectItem value="Oiler" data-testid="rank-oiler">Oiler</SelectItem>
                    <SelectItem value="Moterman" data-testid="rank-moterman">Moterman</SelectItem>
                    <SelectItem value="Wiper" data-testid="rank-wiper">Wiper</SelectItem>
                    <SelectItem value="Fitter" data-testid="rank-fitter">Fitter</SelectItem>
                    <SelectItem value="Fitter-Welder" data-testid="rank-fitter-welder">Fitter-Welder</SelectItem>
                    <SelectItem value="Other" data-testid="rank-other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="experience" data-testid="experience-input-label">Years of Experience</Label>
                <Input
                  id="experience"
                  type="number"
                  value={formData.years_experience}
                  onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                  data-testid="experience-input"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleUpdate} className="flex-1" data-testid="save-button">
                  Save
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)} className="flex-1" data-testid="cancel-button">
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
